/**
 * FLEET MONITORING — the words both screens use about one till.
 *
 * The list row and the device page answer the same four questions (what is it
 * called, is an update coming, is anything waiting to upload, what has it
 * reported). They answer from this file so the two can never disagree about a
 * single device. Pure: no React, no network.
 */

import type { DeviceHealthProblem } from '@platform/shared-types';
import type { FleetRow } from '../pos/fleet/fleetRows';

export type Tone = 'ok' | 'info' | 'warn' | 'bad' | 'mute';

/**
 * What to call a till. Location · register when the till said so; otherwise the
 * register on its own; otherwise "Register · <tracking id>". Never the long
 * hardware catalogue title — that goes on the small grey line instead.
 */
export function deviceDisplayName(row: FleetRow): string {
  const reg = row.registerName?.trim();
  const loc = row.locationName?.trim();
  if (reg && loc && !reg.toLowerCase().includes(loc.toLowerCase())) return `${loc} · ${reg}`;
  if (reg) return reg;
  if (row.trackingId) return `Register · ${row.trackingId}`;
  return row.deviceId || 'Unnamed till';
}

export interface StatusLine { tone: Tone; label: string; detail: string }

/**
 * Is an update coming, and when will it land?
 *
 * The till's own `updateState` wins when it reported one — it is the only
 * first-hand answer. Without it we derive from version vs target: on target is
 * up to date; behind with auto-update on installs at the next idle moment (no
 * sale open); behind with auto-update off waits for an admin.
 */
export function updateStatus(row: FleetRow): StatusLine {
  const target = row.targetVersion;
  const v = row.updateVersion || target;
  if (row.bootInstalled === false && row.error) {
    return { tone: 'bad', label: 'Boot screen install failed', detail: row.error };
  }
  switch (row.updateState) {
    case 'downloading': return { tone: 'info', label: `Downloading ${v ?? 'update'}`, detail: 'Installs when no sale is open' };
    case 'ready':
    case 'waiting_for_idle': return { tone: 'info', label: `Queued → ${v ?? 'update'}`, detail: 'Installs when no sale is open' };
    case 'installing': return { tone: 'info', label: `Installing ${v ?? 'update'}`, detail: 'Till restarts in a moment' };
    case 'failed': return { tone: 'bad', label: 'Update failed', detail: row.updateError || 'The till reported a failed install' };
    default: break;
  }
  if (!target || !row.appVersion) return { tone: 'mute', label: 'Unknown', detail: 'No target or no version reported' };
  if (!row.drift) {
    return { tone: 'ok', label: 'Up to date', detail: row.targetSource === 'pin' ? `Pinned to ${target}` : `Follows ${row.releaseRing} ring` };
  }
  if (!row.autoUpdate) return { tone: 'warn', label: 'Behind · auto-update off', detail: 'Waits for an admin' };
  return { tone: 'warn', label: `Behind → ${target}`, detail: 'Installs when it next checks in and no sale is open' };
}

/** What the till still holds unsent. `null` = the build does not report it (say so; never "none"). */
export function uploadsWaiting(row: FleetRow): { total: number; sales: number; other: number; oldestAt?: number } | null {
  const p = row.pendingUploads;
  if (!p) return null;
  return { total: p.sales + p.other, sales: p.sales, other: p.other, oldestAt: p.oldestAt };
}

/** Issue Console status words — one vocabulary with SuperAdminAppIssuesQueue's STATUS_LABEL. */
export const ISSUE_STATUS_LABEL: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  awaiting_retest: 'Awaiting Retest',
  awaiting_owner_approval: 'Awaiting Approval',
  resolved: 'Resolved',
  rejected: 'Rejected',
};
export const ISSUE_STATUS_TONE: Record<string, Tone> = {
  new: 'bad', in_progress: 'info', awaiting_retest: 'warn', awaiting_owner_approval: 'warn', resolved: 'ok', rejected: 'mute',
};
const CLOSED = new Set(['resolved', 'rejected']);
/** A problem with no ticket yet (write pending) still counts as open — it is a live problem. */
export const isOpenIssue = (p: DeviceHealthProblem) => !CLOSED.has(p.ticketStatus ?? 'new');

/** The Issue Console page for one ticket. Device-health ids carry no stage prefix. */
export const issueHref = (issueId: string) => `/app-issues/${encodeURIComponent(issueId)}/console`;

/** deviceId → its open problems, from one fleet-wide `/problems?status=open` read. */
export function openIssuesByDevice(problems: DeviceHealthProblem[]): Map<string, DeviceHealthProblem[]> {
  const m = new Map<string, DeviceHealthProblem[]>();
  for (const p of problems) {
    if (!isOpenIssue(p)) continue;
    for (const id of p.deviceIds || []) {
      const list = m.get(id);
      if (list) list.push(p); else m.set(id, [p]);
    }
  }
  return m;
}

/** Status order for "the most urgent of these" — new first. */
const URGENCY = ['new', 'in_progress', 'awaiting_retest', 'awaiting_owner_approval'];
export function mostUrgent(problems: DeviceHealthProblem[]): DeviceHealthProblem | undefined {
  return [...problems].sort((a, b) =>
    URGENCY.indexOf(a.ticketStatus ?? 'new') - URGENCY.indexOf(b.ticketStatus ?? 'new'))[0];
}

/** Tailwind classes per tone, light surface. Green is success only (rubytill-theme). */
export const TONE_PILL: Record<Tone, string> = {
  ok: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200',
  info: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  warn: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  bad: 'bg-[#FFF1F3] text-[#9F1239] ring-1 ring-inset ring-rose-200',
  mute: 'bg-stone-100 text-stone-500 ring-1 ring-inset ring-stone-200',
};
